<template>
  <div class="client-teus-dashboard">
    <!-- Header Section -->
    <div class="dashboard-header">
      <h2 class="dashboard-title">Client-Wise TEUs Dashboard</h2>
      <div class="header-actions">
        <button 
          class="btn btn-primary btn-sm" 
          @click="refreshData"
          :disabled="isLoading"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': isLoading }"></i>
          Refresh
        </button>
        <button 
          class="btn btn-success btn-sm" 
          @click="exportToExcel"
          :disabled="isLoading || !clientData.length"
        >
          <i class="fa fa-file-excel-o"></i>
          Export to Excel
        </button>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2">Loading TEUs data...</p>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      <i class="fa fa-exclamation-triangle"></i>
      {{ errorMessage }}
      <button type="button" class="close" @click="errorMessage = ''">
        <span>&times;</span>
      </button>
    </div>
    <b-card class="formCard">
        <b-row>
            <b-col md="3">
                  <date-range-picker
                    class="mb-1"
                    :always-show-calendars="showCal"
                    v-model="Filter.dateRange"
                    :startDate="startDate"
                    :endDate="endDate"
                    :locale-data="locale"
                    @update="fetchData"
                  />
                </b-col>
                <b-col md="2">
                  <model-list-select
                    class="mb-1"
                    :list="$store.getters['common/getAllBranchList']"
                    id="branchfilter"
                    v-model="Filter.branchfilter"
                    option-value="pickupId"
                    option-text="pickupLocation"
                    placeholder="All"
                    @input="fetchData"
                    :isDisabled="
                      $sessionsStorage.get('designation') != 'Admin' ? true : false
                    "
                   />
                  </b-col>
                  <b-col md="2">
                    <model-list-select
                      class="mb-1"
                      :list="transportTypeList"
                      id="transportTypeFilter"
                      v-model="Filter.transportTypeFilter"
                      option-value="transportTypeVal"
                      option-text="transportTypeText"
                      placeholder="All"
                      @input="fetchData"
                     />
                  </b-col>
                  <b-col md="2">
                    <model-list-select
                      class="mb-1"
                      :list="nominationShipperList"
                      id="is_nomination_shipper"
                      v-model="Filter.is_nomination_shipper"
                      option-value="value"
                      option-text="text"
                      placeholder="All Shippers"
                      @input="fetchData"
                     />
                  </b-col>
                  <b-col md="2">
                    <button 
                      class="btn btn-warning btn-sm mt-2" 
                      @click="clearFilters"
                      :disabled="isLoading"
                    >
                      <i class="fa fa-times"></i>
                      Clear Filters
                    </button>
                  </b-col>
                </b-row>
              </b-card>

    <!-- Dashboard Content -->
    <div v-if="!isLoading && clientData.length > 0" class="dashboard-content">
      <!-- Summary Cards -->
      <div class="row mb-4">
        <div class="col-md-3">
          <div class="summary-card">
            <div class="card-icon bg-primary">
              <i class="fa fa-users"></i>
            </div>
            <div class="card-content">
              <h4>{{ totalClients }}</h4>
              <p>Total Clients</p>
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="summary-card">
            <div class="card-icon bg-success">
              <i class="fa fa-cubes"></i>
            </div>
            <div class="card-content">
              <h4>{{ totalTeus }}</h4>
              <p>Total TEUs</p>
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="summary-card">
            <div class="card-icon bg-info">
              <i class="fa fa-ship"></i>
            </div>
            <div class="card-content">
              <h4>{{ totalExportTeus }}</h4>
              <p>Export TEUs</p>
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="summary-card">
            <div class="card-icon bg-warning">
              <i class="fa fa-truck"></i>
            </div>
            <div class="card-content">
              <h4>{{ totalImportTeus }}</h4>
              <p>Import TEUs</p>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="summary-card">
            <div class="card-icon bg-info">
              <i class="fa fa-line-chart"></i>
            </div>
            <div class="card-content">
              <h4>{{ averageTeusPerClient }}</h4>
              <p>Avg TEUs/Client</p>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="summary-card">
            <div class="card-icon bg-warning">
              <i class="fa fa-star"></i>
            </div>
            <div class="card-content">
              <h4>{{ topClient.name }}</h4>
              <p>Top Client ({{ topClient.teus }} TEUs)</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section - Collapsible -->
      <div class="row mb-4">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header" style="cursor: pointer;" @click="showCharts = !showCharts">
              <h5 class="mb-0">
                <i class="fa" :class="showCharts ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                Charts & Visualizations
                <span class="badge badge-info ml-2">{{ showCharts ? 'Hide' : 'Show' }}</span>
              </h5>
            </div>
            <div class="card-body" v-show="showCharts">
              <!-- Flexible Charts (Two Rows) -->
              <div class="row mb-4">
                <div class="col-md-12">
                  <client-teus-flexible-chart 
                    :chart-data="allClientsChartData"
                    :chart-height="500"
                    chart-type="bar"
                    chart-title="Clients TEUs Overview"
                    chart-icon="fa-bar-chart"
                    :default-limit="20"
                  />
                </div>
              </div>
              
              <div class="row">
                <div class="col-md-12">
                  <client-teus-flexible-chart 
                    :chart-data="allClientsChartData"
                    :chart-height="450"
                    chart-type="bar"
                    chart-title="Top Performers"
                    chart-icon="fa-trophy"
                    :default-limit="10"
                  />
                </div>
                <div class="col-md-12">
                  <client-teus-flexible-chart 
                    :chart-data="allClientsChartData"
                    :chart-height="450"
                    chart-type="pie"
                    chart-title="TEUs Distribution"
                    chart-icon="fa-pie-chart"
                    :default-limit="10"
                  />
                </div>
                <div class="col-md-12">
                  <div class="chart-card">
                    <div class="chart-header">
                      <div class="chart-title">
                        <i class="fa fa-line-chart"></i>
                        Monthly TEUs Trend
                        <span class="badge badge-primary ml-2">{{ selectedTrendClients.length }} Clients</span>
                        <span class="badge badge-info ml-2">{{ dynamicTrendChartHeight }}px</span>
                      </div>
                      <div class="chart-controls">
                        <label class="control-label">Show:</label>
                        <select v-model="trendClientLimit" @change="updateTrendClients" class="form-control form-control-sm">
                          <option value="5">Top 5</option>
                          <option value="10">Top 10</option>
                          <option value="15">Top 15</option>
                          <option value="20">Top 20</option>
                          <option value="all">All</option>
                        </select>
                      </div>
                    </div>
                    <client-teus-line-chart 
                      :chart-data="monthlyTrendChartData"
                      :chart-height="dynamicTrendChartHeight"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Data Table -->
      <div class="table-card">
        <h4 class="table-title">
          <i class="fa fa-table"></i>
          Detailed TEUs Data
          <span class="badge badge-primary ml-2">{{ filteredAndSortedData.length }} Clients</span>
        </h4>
        <div class="table-filters mb-3">
          <div class="row align-items-center">
            <div class="col-md-6">
              <input 
                type="text" 
                class="form-control" 
                v-model="searchQuery"
                placeholder="Search by client name..."
                style="max-width: 300px;"
              >
            </div>
            <div class="col-md-6">
              <div class="color-legend">
                <small class="text-muted">
                  <strong>Color Legend:</strong>
                  <span class="text-success font-weight-bold ml-2">🟢 High (>30 TEUs)</span>
                  <span class="text-info ml-2">🔵 Good (20-30 TEUs)</span>
                  <span class="text-warning ml-2">🟡 Medium (10-20 TEUs)</span>
                  <span class="text-muted ml-2">⚪ Low (<10 TEUs)</span>
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-striped table-bordered table-hover">
            <thead>
              <tr>
                <th rowspan="2" class="excel-header">S.No</th>
                <th rowspan="2" class="excel-header" @click="sortBy('clientName')" style="cursor: pointer;">
                  Client Name 
                  <i class="fa fa-sort" v-if="sortColumn !== 'clientName'"></i>
                  <i class="fa fa-sort-asc" v-if="sortColumn === 'clientName' && sortDirection === 'asc'"></i>
                  <i class="fa fa-sort-desc" v-if="sortColumn === 'clientName' && sortDirection === 'desc'"></i>
                </th>
                <th v-for="month in uniqueMonths" :key="month" :colspan="Filter.transportTypeFilter === 'All' ? 3 : Filter.transportTypeFilter === 'Export' ? 1 : 1" class="excel-month-header">
                  {{ month }}
                </th>
                <th rowspan="2" class="excel-total-header" @click="sortBy('totalExport')" style="cursor: pointer;" v-if="Filter.transportTypeFilter === 'Export' || Filter.transportTypeFilter === 'All'">
                  Export Total
                  <i class="fa fa-sort" v-if="sortColumn !== 'totalExport'"></i>
                  <i class="fa fa-sort-asc" v-if="sortColumn === 'totalExport' && sortDirection === 'asc'"></i>
                  <i class="fa fa-sort-desc" v-if="sortColumn === 'totalExport' && sortDirection === 'desc'"></i>
                </th>
                <th rowspan="2" class="excel-total-header" @click="sortBy('totalImport')" style="cursor: pointer;" v-if="Filter.transportTypeFilter === 'Import' || Filter.transportTypeFilter === 'All'">
                  Import Total
                  <i class="fa fa-sort" v-if="sortColumn !== 'totalImport'"></i>
                  <i class="fa fa-sort-asc" v-if="sortColumn === 'totalImport' && sortDirection === 'asc'"></i>
                  <i class="fa fa-sort-desc" v-if="sortColumn === 'totalImport' && sortDirection === 'desc'"></i>
                </th>
                <th rowspan="2" class="excel-total-header" @click="sortBy('total')" style="cursor: pointer;" v-if="Filter.transportTypeFilter === 'All'">
                  Total
                  <i class="fa fa-sort" v-if="sortColumn !== 'total'"></i>
                  <i class="fa fa-sort-asc" v-if="sortColumn === 'total' && sortDirection === 'asc'"></i>
                  <i class="fa fa-sort-desc" v-if="sortColumn === 'total' && sortDirection === 'desc'"></i>
                </th>
              </tr>
              <tr>
                <template v-for="month in uniqueMonths">
                  <th class="excel-sub-header excel-total" v-if="Filter.transportTypeFilter === 'All'">Total</th>
                  <th class="excel-sub-header excel-export" v-if="Filter.transportTypeFilter === 'Export' || Filter.transportTypeFilter === 'All'">Export</th>
                  <th class="excel-sub-header excel-import" v-if="Filter.transportTypeFilter === 'Import' || Filter.transportTypeFilter === 'All'">Import</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(client, index) in filteredAndSortedData" :key="index">
                <td>{{ index + 1 }}</td>
                <td><strong>{{ client.clientName }}</strong><br/>
                <span>Sales Person:{{client.salesPersonName}}</span><br/>
                <template v-if="client.coloderType">
                  <span>Type:{{client.coloderType}}</span><br/>
                </template>
                <template v-else-if="client.clientType">
                  <span>Type:{{client.clientType}}</span><br/>
                </template>
                </td>
                <template v-for="month in uniqueMonths">
                  <td 
                    v-if="Filter.transportTypeFilter === 'All'"
                    class="excel-cell excel-total-cell"
                    :class="getTeusClass(client.monthsMap[month])"
                    :title="getTeusTooltip(client.monthsMap[month])"
                  >
                    {{ client.monthsMap[month] || '-' }}
                  </td>
                  <td 
                    v-if="Filter.transportTypeFilter === 'Export' || Filter.transportTypeFilter === 'All'"
                    class="excel-cell excel-export-cell"
                    :class="getTeusClass(client.exportMonthsMap[month])"
                    :title="getTeusTooltip(client.exportMonthsMap[month])"
                  >
                    {{ client.exportMonthsMap[month] || '-' }}
                  </td>
                  <td 
                    v-if="Filter.transportTypeFilter === 'Import' || Filter.transportTypeFilter === 'All'"
                    class="excel-cell excel-import-cell"
                    :class="getTeusClass(client.importMonthsMap[month])"
                    :title="getTeusTooltip(client.importMonthsMap[month])"
                  >
                    {{ client.importMonthsMap[month] || '-' }}
                  </td>
                </template>
                <td class="excel-cell excel-total-cell" v-if="Filter.transportTypeFilter === 'Export' || Filter.transportTypeFilter === 'All'"><strong>{{ client.totalExport }}</strong></td>
                <td class="excel-cell excel-import-cell" v-if="Filter.transportTypeFilter === 'Import' || Filter.transportTypeFilter === 'All'"><strong>{{ client.totalImport }}</strong></td>
                <td class="excel-cell excel-total-cell" v-if="Filter.transportTypeFilter === 'All'"><strong>{{ client.total }}</strong></td>
              </tr>
            </tbody>
            <tfoot v-if="filteredAndSortedData.length > 0">
              <tr class="excel-footer-row">
                <td colspan="2" class="excel-footer-label" ><strong>Total</strong></td>
                <template v-for="month in uniqueMonths">
                  <td class="excel-cell excel-total-cell" v-if="Filter.transportTypeFilter === 'All'">
                    <strong>{{ getMonthTotal(month) }}</strong>
                  </td>
                  <td class="excel-cell excel-export-cell" v-if="Filter.transportTypeFilter === 'Export' || Filter.transportTypeFilter === 'All'">
                    <strong>{{ getMonthExportTotal(month) }}</strong>
                  </td>
                  <td class="excel-cell excel-import-cell" v-if="Filter.transportTypeFilter === 'Import' || Filter.transportTypeFilter === 'All'">
                    <strong>{{ getMonthImportTotal(month) }}</strong>
                  </td>
                </template>
                <td class="excel-cell excel-export-cell" v-if="Filter.transportTypeFilter === 'Export' || Filter.transportTypeFilter === 'All'"><strong>{{ totalExportTeus }}</strong></td>
                <td class="excel-cell excel-import-cell" v-if="Filter.transportTypeFilter === 'Import' || Filter.transportTypeFilter === 'All'"><strong>{{ totalImportTeus }}</strong></td>
                <td class="excel-cell excel-total-cell" v-if="Filter.transportTypeFilter === 'All'"><strong>{{ totalTeus }}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-if="filteredAndSortedData.length === 0" class="text-center py-4">
          <p class="text-muted">No data found matching your search criteria.</p>
        </div>
      </div>
    </div>

    <!-- No Data Message -->
    <div v-if="!isLoading && clientData.length === 0 && !errorMessage" class="no-data-container">
      <i class="fa fa-inbox fa-3x text-muted mb-3"></i>
      <h4>No Data Available</h4>
      <p class="text-muted">There is no TEUs data available at the moment.</p>
      <button class="btn btn-primary" @click="refreshData">
        <i class="fa fa-refresh"></i>
        Try Again
      </button>
    </div>
  </div>
</template>

<script>
import ClientTeusService from '../../services/ClientTeusService'
import ClientTeusBarChart from './ClientTeusBarChart.vue'
import ClientTeusLineChart from './ClientTeusLineChart.vue'
import ClientTeusAllClientsChart from './ClientTeusAllClientsChart.vue'
import ClientTeusFlexibleChart from './ClientTeusFlexibleChart.vue'
import * as XLSX from 'xlsx'
import { ModelListSelect } from "vue-search-select"
import DateRangePicker from "vue2-daterange-picker"
import "vue2-daterange-picker/dist/vue2-daterange-picker.css"

export default {
  name: 'ClientTeusDashboard',
  beforeMount() {
    this.$store.dispatch(
        "common/setAllBranchList",
        JSON.stringify({
          filter: "BranchList",
          active: "all",
        })
      );
      this.$store.dispatch(
        "common/setAllClientList",
        JSON.stringify({
          itemCode: "ClientList",
        })
      );
    this.$store.dispatch("setCurrentRoute", "/dashboard/client-teus-dashboard");
    if (
      this.$sessionsStorage.get("loginId") != undefined &&
      this.$sessionsStorage.get("loginId") != "" &&
      this.$sessionsStorage.get("departmentName") != undefined &&
      this.$sessionsStorage.get("departmentName") != "" &&
      this.$sessionsStorage.get("designation") != undefined &&
      this.$sessionsStorage.get("designation") != ""
    ) {
      this.fetchData();
    } else {
      this.$sessionsStorage.set("loginId", "");
      this.$router.push({
        path: "/Login/",
      });
    }
  },
  components: {
    ClientTeusBarChart,
    ClientTeusLineChart,
    ClientTeusAllClientsChart,
    ClientTeusFlexibleChart,
    ModelListSelect,
    DateRangePicker
  },

  data() {
    return {
      openLeft: "left",
      showCal: false,
      startDate: "2017-09-05",
      endDate: "2017-09-15",
      Filter: {
        transportTypeFilter: "All",
        branchfilter: "",
        is_nomination_shipper: "",
        dateRange: {
          startDate: "",
          endDate: "",
        },
      },
      locale: {
        direction: "ltr",
        format: "mm/dd/yyyy",
        separator: " - ",
        applyLabel: "Apply",
        cancelLabel: "Cancel",
        weekLabel: "W",
        customRangeLabel: "Custom Range",
        daysOfWeek: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        monthNames: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
        firstDay: 0,
      },
      value1: [new Date(2019, 9, 8), new Date(2019, 9, 19)],
      value2: [],
      langType: {
        pickers: ["Today", "Yesterday", "This Month", "This Year"],
      },
      time: "",
      date: "",
      week: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],

      langEnq: {
        pickers: ["", "", "previous 7 days", "previous 30 days"],
      },
        transportTypeList: [
        {
          transportTypeVal: "Export",
          transportTypeText: "Export"
        },
        {
          transportTypeVal: "Import",
          transportTypeText: "Import"
        },
        {
          transportTypeVal: "All",
          transportTypeText: "All"
        }
      ],
      nominationShipperList: [
        {
          value: "1",
          text: "Nominated Shippers"
        },
        {
          value: "0",
          text: "Free Hand Shippers"
        },
        {
          value: "",
          text: "All Shippers"
        }
      ],

      clientData: [],
      isLoading: false,
      errorMessage: '',
      searchQuery: '',
      sortColumn: 'total',
      sortDirection: 'desc',
      showCharts: false,
      trendClientLimit: '5',
      selectedTrendClients: []
    }
  },

  computed: {
    // Get unique months from all clients
    uniqueMonths() {
      const monthsSet = new Set()
      this.clientData.forEach(client => {
        client.months.forEach(m => {
          monthsSet.add(m.month + ' ' + m.year)
        })
      })
      return Array.from(monthsSet).sort((a, b) => {
        const [monthA, yearA] = a.split(' ')
        const [monthB, yearB] = b.split(' ')
        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB)
        return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB)
      })
    },

    // Process data for table display
    processedTableData() {
      return this.clientData.map(client => {
        const monthsMap = {}
        const exportMonthsMap = {}
        const importMonthsMap = {}
        let total = 0
        let totalExport = 0
        let totalImport = 0
        
        client.months.forEach(m => {
          const key = m.month + ' ' + m.year
          monthsMap[key] = m.totalTeus
          exportMonthsMap[key] = m.exportTeus || 0
          importMonthsMap[key] = m.importTeus || 0
          total += m.totalTeus
          totalExport += m.exportTeus || 0
          totalImport += m.importTeus || 0
        })

        return {
          clientName: client.clientName,
          salesPersonName:client.salesPersonName,
          clientType:client.clientType,
          coloderType:client.coloderType,
          monthsMap,
          exportMonthsMap,
          importMonthsMap,
          total,
          totalExport,
          totalImport
        }
      })
    },

    // Filter and sort data
    filteredAndSortedData() {
      let data = this.processedTableData

      // Apply search filter
      if (this.searchQuery) {
        data = data.filter(item => 
          item.clientName.toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      }

      // Apply sorting
      if (this.sortColumn) {
        data.sort((a, b) => {
          let aVal, bVal
          
          if (this.sortColumn === 'clientName') {
            aVal = a.clientName.toLowerCase()
            bVal = b.clientName.toLowerCase()
          } else if (this.sortColumn === 'totalExport') {
            aVal = a.totalExport
            bVal = b.totalExport
          } else if (this.sortColumn === 'totalImport') {
            aVal = a.totalImport
            bVal = b.totalImport
          } else {
            aVal = a.total
            bVal = b.total
          }

          if (this.sortDirection === 'asc') {
            return aVal > bVal ? 1 : -1
          } else {
            return aVal < bVal ? 1 : -1
          }
        })
      }

      return data
    },

    // Summary statistics
    totalClients() {
      return this.clientData.filter(c => c.months.length > 0).length
    },

    totalTeus() {
      return this.clientData.reduce((sum, client) => {
        return sum + client.months.reduce((s, m) => s + m.totalTeus, 0)
      }, 0)
    },

    totalExportTeus() {
      return this.clientData.reduce((sum, client) => {
        return sum + client.months.reduce((s, m) => s + (m.exportTeus || 0), 0)
      }, 0)
    },

    totalImportTeus() {
      return this.clientData.reduce((sum, client) => {
        return sum + client.months.reduce((s, m) => s + (m.importTeus || 0), 0)
      }, 0)
    },

    averageTeusPerClient() {
      return this.totalClients > 0 
        ? Math.round(this.totalTeus / this.totalClients) 
        : 0
    },

    topClient() {
      let top = { name: 'N/A', teus: 0 }
      this.processedTableData.forEach(client => {
        if (client.total > top.teus) {
          top = { name: client.clientName, teus: client.total }
        }
      })
      return top
    },

    // Chart data for all clients (horizontal bar chart)
    allClientsChartData() {
      const sortedData = Object.assign([], this.processedTableData)
        .sort((a, b) => b.total - a.total) // Sort by total descending

      return {
        labels: sortedData.map(c => c.clientName),
        datasets: [{
          label: 'Total TEUs',
          data: sortedData.map(c => c.total),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }]
      }
    },

    // Chart data for bar chart (top 10)
    clientTeusChartData() {
      const sortedData = Object.assign([], this.processedTableData)
        .sort((a, b) => b.total - a.total)
        .slice(0, 10) // Top 10 clients

      return {
        labels: sortedData.map(c => c.clientName),
        datasets: [{
          label: 'Total TEUs',
          data: sortedData.map(c => c.total),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }]
      }
    },

    // Chart data for line chart
    monthlyTrendChartData() {
      const months = this.uniqueMonths
      const datasets = []

      // Use selected trend clients or default to top 5
      const clientsToShow = this.selectedTrendClients.length > 0 
        ? this.selectedTrendClients 
        : Object.assign([], this.processedTableData)
            .sort((a, b) => b.total - a.total)
            .slice(0, 5)

      const colors = [
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)',
        'rgba(255, 206, 86, 1)',
        'rgba(75, 192, 192, 1)',
        'rgba(153, 102, 255, 1)',
        'rgba(255, 159, 64, 1)',
        'rgba(199, 199, 199, 1)',
        'rgba(83, 102, 255, 1)',
        'rgba(255, 99, 255, 1)',
        'rgba(99, 255, 132, 1)',
        'rgba(255, 132, 99, 1)',
        'rgba(132, 99, 255, 1)',
        'rgba(255, 255, 99, 1)',
        'rgba(99, 255, 255, 1)',
        'rgba(255, 99, 99, 1)',
        'rgba(99, 99, 255, 1)',
        'rgba(255, 255, 132, 1)',
        'rgba(132, 255, 99, 1)',
        'rgba(255, 132, 255, 1)',
        'rgba(132, 255, 255, 1)'
      ]

      clientsToShow.forEach((client, index) => {
        datasets.push({
          label: client.clientName,
          data: months.map(month => client.monthsMap[month] || 0),
          borderColor: colors[index % colors.length],
          backgroundColor: colors[index % colors.length].replace('1)', '0.1)'),
          tension: 0.4,
          fill: false
        })
      })

      return {
        labels: months,
        datasets
      }
    },

    // Dynamic height for trend chart based on number of clients
    dynamicTrendChartHeight() {
      const baseHeight = 450
      const clientCount = this.selectedTrendClients.length || 5
      
      // For line charts, increase height based on number of clients
      const heightPerClient = 30 // 30px per client
      const minHeight = 350
      const maxHeight = 800
      const calculatedHeight = baseHeight + (clientCount * heightPerClient)
      
      return Math.max(minHeight, Math.min(maxHeight, calculatedHeight))
    }
  },

  mounted() {
    this.fetchData()
  },

  watch: {
    processedTableData() {
      // Update trend clients when data changes
      this.updateTrendClients()
    }
  },

  methods: {
    async fetchData() {
      this.isLoading = true
      this.errorMessage = ''

      try {

        const response = await ClientTeusService.getClientWiseTeus(this.Filter)
        
        if (response.data && response.data.status) {
          this.clientData = response.data.data || []
        } else {
          this.errorMessage = response.data.message || 'Failed to fetch TEUs data'
        }
      } catch (error) {
        console.error('Error fetching TEUs data:', error)
        this.errorMessage = 'Failed to fetch TEUs data. Please try again later.'
      } finally {
        this.isLoading = false
      }
    },

    refreshData() {
      this.fetchData()
    },

    clearFilters() {
      this.Filter = {
        transportTypeFilter: "All",
        branchfilter: "",
        is_nomination_shipper: "",
        dateRange: {
          startDate: "",
          endDate: "",
        },
      }
      this.fetchData()
    },

    updateTrendClients() {
      const limit = this.trendClientLimit === 'all' 
        ? this.processedTableData.length 
        : parseInt(this.trendClientLimit)

      this.selectedTrendClients = Object.assign([], this.processedTableData)
        .sort((a, b) => b.total - a.total)
        .slice(0, limit)
    },

    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortDirection = 'desc'
      }
    },

    getTeusClass(value) {
      if (!value) return ''
      if (value > 30) return 'text-success font-weight-bold'
      if (value > 20) return 'text-info'
      if (value > 10) return 'text-warning'
      return 'text-muted'
    },

    getTeusTooltip(value) {
      if (!value) return 'No data available'
      if (value > 30) return 'High Volume: ' + value + ' TEUs (Excellent Performance)'
      if (value > 20) return 'Good Volume: ' + value + ' TEUs (Good Performance)'
      if (value > 10) return 'Medium Volume: ' + value + ' TEUs (Average Performance)'
      return 'Low Volume: ' + value + ' TEUs (Below Average Performance)'
    },

    getMonthTotal(month) {
      return this.filteredAndSortedData.reduce((sum, client) => {
        return sum + (client.monthsMap[month] || 0)
      }, 0)
    },

    getMonthExportTotal(month) {
      return this.filteredAndSortedData.reduce((sum, client) => {
        return sum + (client.exportMonthsMap[month] || 0)
      }, 0)
    },

    getMonthImportTotal(month) {
      return this.filteredAndSortedData.reduce((sum, client) => {
        return sum + (client.importMonthsMap[month] || 0)
      }, 0)
    },

    exportToExcel() {
      try {
        // Prepare data for Excel
        const excelData = []

        // Add header rows
        const headerRow1 = ['S.No', 'Client Name','Client Type','Agent Name', ...this.uniqueMonths.map(month => [month, '', '']).flat(), 'Export Total', 'Import Total', 'Total']
        excelData.push(headerRow1)
        
        const headerRow2 = ['', '','','', ...this.uniqueMonths.map(month => ['Total', 'Export', 'Import']).flat(), '', '', '']
        excelData.push(headerRow2)

        // Add data rows
        this.filteredAndSortedData.forEach((client, index) => {
          const row = [
            index + 1,
            client.clientName,
            client.salesPersonName,
            client.clientType,
            client.coloderType,
            ...this.uniqueMonths.map(month => [
              client.monthsMap[month] || 0,
              client.exportMonthsMap[month] || 0,
              client.importMonthsMap[month] || 0
            ]).flat(),
            client.totalExport,
            client.totalImport,
            client.total
          ]
          excelData.push(row)
        })

        // Add total row
        const totalRow = [
          '',
          'Total',
          ...this.uniqueMonths.map(month => [
            this.getMonthTotal(month),
            this.getMonthExportTotal(month),
            this.getMonthImportTotal(month)
          ]).flat(),
          this.totalExportTeus,
          this.totalImportTeus,
          this.totalTeus
        ]
        excelData.push(totalRow)

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(excelData)

        // Set column widths
        ws['!cols'] = [
          { wch: 6 },  // S.No
          { wch: 40 }, // Client Name
          ...this.uniqueMonths.map(() => [
            { wch: 10 }, // Total
            { wch: 10 }, // Export
            { wch: 10 }  // Import
          ]).flat(),
          { wch: 15 }, // Export Total
          { wch: 15 }, // Import Total
          { wch: 12 }  // Total
        ]

        // Create workbook
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Client TEUs Data')

        // Generate filename with current date
        const date = new Date()
        const filename = 'Client_TEUs_' + date.getFullYear() + 
          ('0' + (date.getMonth() + 1)).slice(-2) + 
          ('0' + date.getDate()).slice(-2) + '.xlsx'

        // Write file
        XLSX.writeFile(wb, filename)
      } catch (error) {
        console.error('Error exporting to Excel:', error)
        this.errorMessage = 'Failed to export to Excel. Please try again.'
      }
    }
  }
}
</script>

<style scoped>
.client-teus-dashboard {
  padding: 20px;
  background-color: #f4f6f9;
  min-height: 100vh;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.dashboard-title {
  margin: 0;
  color: #333;
  font-size: 24px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.header-actions .btn {
  padding: 8px 16px;
  font-size: 14px;
}

.loading-container {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.dashboard-content {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Summary Cards */
.summary-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.summary-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.card-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.card-icon.bg-primary { background-color: #007bff; }
.card-icon.bg-success { background-color: #28a745; }
.card-icon.bg-info { background-color: #17a2b8; }
.card-icon.bg-warning { background-color: #ffc107; }

.card-content h4 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.card-content p {
  margin: 5px 0 0 0;
  font-size: 14px;
  color: #666;
}

/* Chart Cards */
.chart-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.chart-title {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
}

.chart-title i {
  margin-right: 8px;
  color: #007bff;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #007bff;
}

.chart-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin: 0;
}

/* Table Card */
.table-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.table-title {
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 700;
  color: #333;
  border-bottom: 3px solid #007bff;
  padding-bottom: 15px;
  display: flex;
  align-items: center;
}

.table-title i {
  margin-right: 10px;
  color: #007bff;
}

.table {
  margin-bottom: 0;
}

        /* Excel-like Table Styling */
        .excel-header {
          background-color: #4472C4 !important;
          color: white !important;
          font-weight: 700;
          font-size: 12px;
          border: 1px solid #2F4F7F;
          text-align: center;
          padding: 8px 6px;
          position: sticky;
          top: 0;
          z-index: 10;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .excel-month-header {
          background-color: #4472C4 !important;
          color: white !important;
          font-weight: 700;
          font-size: 12px;
          border: 1px solid #2F4F7F;
          text-align: center;
          padding: 8px 6px;
          position: sticky;
          top: 0;
          z-index: 10;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .excel-total-header {
          background-color: #4472C4 !important;
          color: white !important;
          font-weight: 700;
          font-size: 12px;
          border: 1px solid #2F4F7F;
          text-align: center;
          padding: 8px 6px;
          position: sticky;
          top: 0;
          z-index: 10;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .excel-sub-header {
          font-weight: 600;
          font-size: 11px;
          border: 1px solid #2F4F7F;
          text-align: center;
          padding: 6px 4px;
          position: sticky;
          top: 40px;
          z-index: 9;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .excel-sub-header.excel-total {
          background-color: #D9E2F3 !important;
          color: #2F4F7F !important;
        }

        .excel-sub-header.excel-export {
          background-color: #E2EFDA !important;
          color: #2F4F7F !important;
        }

        .excel-sub-header.excel-import {
          background-color: #FFF2CC !important;
          color: #2F4F7F !important;
        }

        .excel-cell {
          border: 1px solid #D0D7DE;
          text-align: center;
          padding: 6px 4px;
          font-size: 11px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          vertical-align: middle;
        }

        .excel-total-cell {
          background-color: #F8F9FA;
        }

        .excel-export-cell {
          background-color: #F0F8FF;
        }

        .excel-import-cell {
          background-color: #FFFEF0;
        }

        .excel-footer-row {
          background-color: #E7E6E6 !important;
        }

        .excel-footer-label {
          background-color: #E7E6E6 !important;
          font-weight: 700;
          border: 1px solid #2F4F7F;
          text-align: center;
          padding: 8px 6px;
        }

        .table thead th:first-child {
          text-align: left;
          padding-left: 15px;
        }

        .table thead th:nth-child(2) {
          text-align: left;
          padding-left: 15px;
        }

.table tbody tr:hover {
  background-color: #f8f9fa;
}

.table tbody td {
  cursor: help;
  transition: all 0.2s ease;
}

.table tbody td:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.color-legend {
  text-align: right;
}

@media (max-width: 768px) {
  .color-legend {
    text-align: left;
    margin-top: 10px;
  }
  
  .color-legend small {
    display: block;
    margin-bottom: 5px;
  }
}

.no-data-container {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.no-data-container h4 {
  color: #333;
  margin-bottom: 10px;
}

@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .header-actions .btn {
    width: 100%;
  }

  .summary-card {
    margin-bottom: 15px;
  }

  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .chart-controls {
    width: 100%;
    justify-content: space-between;
  }
  
  .chart-title {
    font-size: 16px;
  }
}
</style>




// WEBPACK FOOTER //
// ClientTeusDashboard.vue?8ef198fc